import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState, Icon, Skeleton, Table, Tag } from "@xinghai/ui";
import type { Column } from "@xinghai/ui";
import { arbitrationApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import {
  DOMAIN_LABEL,
  TASK_LABEL_EXPERT,
  formatDuration,
  formatWaiting,
} from "../annotator/labels";
import type { ArbitrationQueueRow } from "../annotator/types";

export function arbitrationHref(recordingId: string): string {
  return `/j/${encodeURIComponent(recordingId)}`;
}

/** docs/UX.md 9 — 仲裁队列. */
export function ArbitrationQueuePage() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: qk.arbitrationQueue, queryFn: () => arbitrationApi.queue() });
  const items = q.data?.items ?? [];

  const columns: ReadonlyArray<Column<ArbitrationQueueRow>> = [
    {
      key: "recording_id",
      header: "录音编号",
      render: (r) => <span className="gz-type-data">{r.recording_id}</span>,
    },
    {
      key: "task",
      header: "任务",
      hideOnMobile: true,
      render: (r) => (
        <Tag tone="info">
          {TASK_LABEL_EXPERT[r.task]} · {DOMAIN_LABEL[r.domain]}
        </Tag>
      ),
    },
    {
      key: "material",
      header: "素材标题",
      render: (r) => r.material?.title ?? r.material?.text ?? "—",
    },
    {
      key: "conflicts",
      header: "冲突数",
      align: "end",
      render: (r) => (
        <Tag tone={r.conflict_count > 8 ? "danger" : "warning"}>{r.conflict_count}</Tag>
      ),
    },
    {
      key: "waiting",
      header: "等待时长",
      align: "end",
      render: (r) => formatWaiting(r.waiting_since),
    },
    {
      key: "duration",
      header: "时长",
      align: "end",
      hideOnMobile: true,
      render: (r) => formatDuration(r.duration_ms),
    },
    {
      key: "go",
      header: "操作",
      align: "end",
      render: (r) => (
        <Link className="gz-button gz-button--primary" to={arbitrationHref(r.recording_id)}>
          处理
        </Link>
      ),
    },
  ];

  return (
    <div className="xz-container xz-stack xz-queue">
      <header className="xz-queue__head">
        <h1 className="gz-type-heading">仲裁队列</h1>
        <dl className="xz-queue__counts">
          <div>
            <dt>待仲裁</dt>
            <dd className="gz-type-score">{q.data?.counts.queued ?? items.length}</dd>
          </div>
        </dl>
      </header>

      {q.isPending ? (
        <div aria-busy="true" className="xz-stack">
          <Skeleton variant="rect" height={56} />
          <Skeleton variant="rect" height={56} />
        </div>
      ) : q.isError ? (
        <p className="gz-notice gz-notice--error" role="alert">
          <Icon id="ui-error" size={20} /> 队列没有加载出来，请刷新重试。
        </p>
      ) : (
        <Table<ArbitrationQueueRow>
          caption="待仲裁录音"
          columns={columns}
          rows={items}
          rowKey={(r) => r.recording_id}
          onRowClick={(r) => void navigate(arbitrationHref(r.recording_id))}
          empty={
            <EmptyState
              icon={<Icon id="ui-check-circle" size={24} />}
              title="没有待仲裁的录音"
              description="两位专家的判断分歧超过阈值时，录音才会进入这里。"
            />
          }
        />
      )}
    </div>
  );
}
